import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axios } from '@/lib/axios/axios';
import { MESSAGING_API_BASE, messagingQueryKeys } from '../config';
import { MarkAsReadBody } from '../types/backend';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/types';

export type MarkAsReadDTO = {
  channelId: number;
  messageId: number;
};

const markAsRead = async (
  data: MarkAsReadDTO
): Promise<{ success: boolean }> => {
  const { channelId, messageId } = data;

  const body: MarkAsReadBody = {
    'message-id': messageId,
  };

  return axios.post(`${MESSAGING_API_BASE}/channels/${channelId}/read`, body);
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  const currentUserId = useSelector(
    (store: RootState) => store.loggedUser?.data?.id
  );

  return useMutation({
    mutationFn: (data: MarkAsReadDTO) => markAsRead(data),
    onSuccess: (_, variables) => {
      // Optimistically update unread counts immediately
      queryClient.setQueryData(
        messagingQueryKeys.unreadCounts(),
        (old: Record<number, number> | undefined) => {
          if (!old) return old;
          // Marking a specific message as read implies the backend marks that
          // message and all previous messages as read for the current user.
          // Therefore, optimistically set unread count to 0 for this channel.
          return { ...old, [variables.channelId]: 0 };
        }
      );

      // Optimistically update message read status in cache
      // This ensures the sender sees "Read" status immediately when recipient marks as read
      if (currentUserId) {
        // Mark all messages up to and including the specified messageId as read
        // in the cached messages for this channel. This mirrors backend
        // behaviour where marking a message as read marks all previous ones.
        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const key = query.queryKey as any[];
              return (
                key?.[0] === 'messaging' &&
                key.some((k) => k === 'messages') &&
                key.some((k) => k === variables.channelId)
              );
            },
          },
          (old: any) => {
            if (!old?.pages) return old;

            const targetId = Number(variables.messageId);

            const newPages = old.pages.map((page: any[]) =>
              page.map((msg: any) => {
                const msgId = Number(msg.id || msg['message-id'] || 0);
                if (msgId <= targetId) {
                  const currentReadByUserIds = msg['read-by-user-ids'] || [];
                  if (!currentReadByUserIds.includes(currentUserId)) {
                    return {
                      ...msg,
                      'read-by-user-ids': [
                        ...currentReadByUserIds,
                        currentUserId,
                      ],
                      'date-updated': new Date().toISOString(),
                    };
                  }
                }
                return msg;
              })
            );

            return {
              ...old,
              pages: newPages,
            };
          }
        );
      }

      // NOTE: We rely on socket events (MESSAGING_UNREAD_COUNT and MESSAGING_MESSAGE_READ)
      // to sync the correct state from the backend. The optimistic updates above provide
      // instant feedback. We don't invalidate queries here to avoid race conditions and
      // duplicate API calls.

      // NOTE: We also don't invalidate channels list to avoid unnecessary refetches
      // Unread badges will update via the optimistic update above + socket events
    },
  });
};
